import {useCallback, useSyncExternalStore} from 'react'

import * as persisted from '#/state/persisted'
import {
  type ClassroomAudienceState,
  CLASSROOM_AUDIENCE_STATE_VERSION,
  type ClassroomPostAudience,
  readPostAudience,
} from './types'

/**
 * The audience an author asked the composer to remember.
 *
 * A post written in a classroom is addressed to somebody — the room's members, or
 * its teachers, or everybody — and the answer is usually the same answer as last
 * time. Asking it every time and keeping nothing would be the app forgetting
 * something it was told, and remembering it every time would be the app deciding
 * for the author: the teacher who narrowed one exam paper to the students has not
 * said anything about the announcement after it.
 *
 * So it is kept, and only when asked for. What is kept is deliberately small — one
 * answer per classroom per account — because the question it answers is *what did
 * I mean last time in this room*, and that is the whole of it: the classroom's own
 * visibility is the default (`defaultPostAudience`), and this only ever overrides
 * it for the author who chose to.
 *
 * Kept on the device beside the classrooms themselves, for the same reason they
 * are: there is no service to keep it in yet, and the composer that reads it is the
 * one that writes the post's record, so the two cannot disagree about which answer
 * was remembered.
 */

const listeners = new Set<() => void>()
let cache: ClassroomAudienceState | null = null
let listeningToStorage = false

function stored(): ClassroomAudienceState {
  return (
    persisted.get('classroomAudience') ?? {
      version: CLASSROOM_AUDIENCE_STATE_VERSION,
      byDid: {},
    }
  )
}

function emit() {
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  if (!listeningToStorage) {
    listeningToStorage = true
    // Another tab (web) may have written while this one was open.
    persisted.onUpdate('classroomAudience', () => {
      cache = null
      emit()
    })
  }
  return () => {
    listeners.delete(listener)
  }
}

/**
 * Every answer this device holds, normalized once.
 *
 * Once, and not on each reading, because the composer reads this through
 * `useSyncExternalStore` (`useStoredPostAudience`): that hook asks *what is the
 * value now?* on every render and re-renders when the answer is not the same
 * object as last time, so a reader that built a fresh list out of the same words
 * each time would spin forever. The normalization (`readPostAudience`) therefore
 * happens here, where the result is held until something writes, and every reading
 * in between is handed the very same list.
 */
function getAll(): ClassroomAudienceState {
  if (cache) return cache
  const state = stored()
  /*
   * A record written by a build that stored something else — or by hand — is read
   * as no answer rather than trusted far enough to address a post with.
   */
  if (
    !state ||
    typeof state !== 'object' ||
    !state.byDid ||
    Array.isArray(state.byDid)
  ) {
    cache = {version: CLASSROOM_AUDIENCE_STATE_VERSION, byDid: {}}
    return cache
  }
  const byDid: ClassroomAudienceState['byDid'] = {}
  for (const [did, answers] of Object.entries(state.byDid)) {
    if (!answers || typeof answers !== 'object' || Array.isArray(answers))
      continue
    const kept: Record<string, ClassroomPostAudience> = {}
    for (const [classroomId, answer] of Object.entries(answers)) {
      const audience = readPostAudience(answer)
      if (audience) kept[classroomId] = audience
    }
    byDid[did] = kept
  }
  cache = {version: CLASSROOM_AUDIENCE_STATE_VERSION, byDid}
  return cache
}

/**
 * The audience this account kept for a classroom, or `undefined` when it never
 * did — which is a real state and is not *public*: the room's own visibility is
 * the answer then (`defaultPostAudience`).
 *
 * The answer was read as the list it means when the device's answers were read
 * (`getAll`), so an answer kept by the build that asked this question with one
 * choice — `public`, `members`, or the name of a single seat — is the list it
 * meant, and an answer that could not be read at all is no answer rather than a
 * wrong one: the room's own default is what the author gets, which is what they
 * had before they ever ticked the box.
 */
export function readStoredPostAudience(
  did: string | undefined,
  classroomId: string | undefined,
): ClassroomPostAudience | undefined {
  if (!did || !classroomId) return undefined
  return getAll().byDid[did]?.[classroomId]
}

/**
 * Write down the audience the author just asked to keep.
 *
 * Written for the account that asked — never for a different one, so a shared
 * device cannot address one person's post with another's answer.
 */
export function setStoredPostAudience(
  did: string,
  classroomId: string,
  audience: ClassroomPostAudience,
): void {
  const state = getAll()
  const next: ClassroomAudienceState = {
    version: CLASSROOM_AUDIENCE_STATE_VERSION,
    byDid: {
      ...state.byDid,
      [did]: {...state.byDid[did], [classroomId]: audience},
    },
  }
  void persisted.write('classroomAudience', next)
  cache = null
  emit()
}

/** The answer this device holds, for the composer that renders on it. */
export function useStoredPostAudience(
  did: string | undefined,
  classroomId: string | undefined,
): ClassroomPostAudience | undefined {
  const getter = useCallback(
    () => readStoredPostAudience(did, classroomId),
    [did, classroomId],
  )
  return useSyncExternalStore(subscribe, getter, getter)
}
